import { Component, Element, Event, EventEmitter, Fragment, h, Host, Method, Prop, State, Watch } from "@stencil/core";

import {
  PanoramaScene,
  PanoramaScenesLibrary,
  StzhDropdownChangeEvent,
  StzhPanoramaChangeEvent,
  StzhPanoramaFullscreenChangeEvent,
  StzhPanoramaLoadEvent,
  StzhPanoramaSceneChangeEvent,
  StzhPanoramaZoomChangeEvent,
} from "../../index";

import { StzhPanoramaLocalizedText } from "./stzh-panorama.localization";

import "pannellum";

const CLASS_HOTSPOT = "stzh-panorama__hotspot";
const CLASS_HOTSPOT_TOOLTIP = "stzh-panorama__hotspot-tooltip";
const CLASS_HOTSPOT_BUTTON = "stzh-panorama__hotspot-button";
const CLASS_HOTSPOT_BUTTON_SCENE = "is-scene";
const CLASS_HOTSPOT_BUTTON_INFO = "is-info";
const CLASS_HOTSPOT_BUTTON_LINK = "is-link";

const ICON_HOTSPOT_SCENE = "arrow-up";
const ICON_HOTSPOT_DEFAULT = "info-help";

/**
 *
 */
@Component({
  tag: "stzh-panorama",
  styleUrl: "stzh-panorama.scss",
})
export class StzhPanorama {
  /** Translation strings */
  @Prop() localization: StzhPanoramaLocalizedText;

  /** Caption */
  @Prop() caption: string = "";

  /** Heading */
  @Prop() heading: string = "";

  /** Copyright */
  @Prop() copyright: string = "";

  /** Current scene */
  @Prop({ mutable: true }) scene: string;

  /** Fade duration on scene change */
  @Prop() sceneFadeDuration = 500;

  /** Whether to autoload panorama on pageload */
  @Prop() autoload: boolean = true;

  /** If set to true, device orientation control will be used when the panorama is loaded, if the device supports it. If false, device orientation control needs to be activated by pressing a button. */
  @Prop() orientationOnByDefault: boolean = false;

  /** If set to false, moving/paning/zooming controls will be hidden. e.g. when embedding the panorama into content columns. */
  @Prop() showControls: boolean = true;

  /** If set to false, hotspots will be hidden. e.g. when embedding the panorama into content columns. */
  @Prop() showHotspots: boolean = true;

  /**
   * Available scenes
   *
   * An array of objects including `sceneId`, `imageTitle`, `imageSrc`, optionally `imageAuthor`, `imageType`, `caption`, `pitch`, `yaw`, `hfov` and `hotspots`
   * `hotspots` can be an array of objects including `pitch`, `yaw`, `type`, `text`, optionally `url`, `id`, `sceneId`, `targetPitch` and `targetYaw`
   *
   * See also https://pannellum.org/documentation/reference/
   */
  @Prop() scenes: PanoramaScene[] | string;
  private _scenes: PanoramaScene[];
  private _scenesLibrary: PanoramaScenesLibrary;

  @Element() element: HTMLStzhFigureElement;

  /** Get viewer instance */
  @Method()
  async getViewer(): Promise<any> {
    return this.viewer;
  }

  @Watch("scenes")
  scenesWatcher(newValue: PanoramaScene[] | string) {
    if (!this.localization) {
      return;
    }

    if (typeof newValue === "string") {
      this._scenes = JSON.parse(newValue);
    } else if (newValue) {
      this._scenes = newValue;
    } else {
      throw new Error('"scenes" is empty');
    }

    const scenesLib: PanoramaScenesLibrary = {};

    // copy object and modify scene hotspots to build own more accessible elements
    this._scenes.forEach(scene => {
      const { sceneId, imageTitle, imageSrc, imageAuthor, imageType, hotspots, ...sceneParams } = scene;

      scenesLib[sceneId] = {
        ...sceneParams,
        type: imageType,
        panorama: imageSrc,
        hotSpots: this.showHotspots
          ? hotspots.map(hotSpot => ({
              ...hotSpot,
              URL: "", // switch hotspot into non url info spot to be able to create accessible link
              cssClass: CLASS_HOTSPOT,
              createTooltipFunc: this.createTooltip,
              createTooltipArgs: { ...hotSpot, url: hotSpot.url },
            }))
          : undefined,
      };
    });

    this._scenesLibrary = scenesLib;
  }

  @Watch("scene")
  sceneWatcher(newValue: string) {
    this.viewer.loadScene(newValue);
  }

  @Watch("autoload")
  @Watch("scenes")
  @Watch("type")
  @Watch("localization")
  @Watch("orientationOnByDefault")
  @Watch("sceneFadeDuration")
  updateWatcher() {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }

  // @Listen("load", { target: "window" })
  // @Listen("deviceorientation", { target: "window" })
  // @Listen("pageshow", { target: "window" })
  // handleLoad() {
  //   if (this.viewer) {
  //     requestAnimationFrame(() => {
  //       this.viewer.resize();
  //     });
  //   }
  // }

  /** Load event */
  @Event() stzhLoad: EventEmitter<StzhPanoramaLoadEvent>;

  /** Change event */
  @Event() stzhChange: EventEmitter<StzhPanoramaChangeEvent>;

  /** Fullscreen change event */
  @Event()
  stzhFullscreenChange: EventEmitter<StzhPanoramaFullscreenChangeEvent>;

  /** Scene change event */
  @Event() stzhSceneChange: EventEmitter<StzhPanoramaSceneChangeEvent>;

  /** Zoom change event */
  @Event() stzhZoomChange: EventEmitter<StzhPanoramaZoomChangeEvent>;

  @State() isOrientationSupported = false;
  @State() isOrientationActive = false;
  @State() isFullscreen = false;

  private viewer: any;
  private containerElement: HTMLElement;
  private actionRunning: { [key: string]: number } = {};
  private controlAnimationTime: number = 500;

  private createTooltip = (hotspotDiv, options) => {
    const element = document.createElement("stzh-button");
    element.classList.add(CLASS_HOTSPOT_BUTTON);

    if (options.type === "scene") {
      element.classList.add(CLASS_HOTSPOT_BUTTON_SCENE);
    } else if (options.url && !options.text) {
      element.classList.add(CLASS_HOTSPOT_BUTTON_LINK);
      element.setAttribute("href", options.url);
      element.setAttribute("target", "_blank");
    } else {
      element.classList.add(CLASS_HOTSPOT_BUTTON_INFO);
    }

    element.icon = options.type === "scene" ? ICON_HOTSPOT_SCENE : ICON_HOTSPOT_DEFAULT;
    element.iconOnly = true;
    element.rounded = true;
    element.size = "small";
    element.variant = "secondary";

    if (options.text) {
      const tooltip = document.createElement("stzh-tooltip");
      tooltip.label = this.localization.hotspotLabel;
      tooltip.placement = "top";
      tooltip.type = "button";
      tooltip.className = CLASS_HOTSPOT_TOOLTIP;

      if (options.url) {
        const tooltipContentSlot = document.createElement("div");
        tooltipContentSlot.slot = "content";

        const text = document.createElement("p");
        if (options.text.length >= 40) {
          text.style["textWrapMode"] = "wrap";
          text.style["textWrap"] = "wrap";
          text.style.minWidth = "200px";
        }
        text.style.marginTop = "0";
        text.style.marginBottom = "4px";
        text.textContent = options.text;
        tooltipContentSlot.appendChild(text);

        const link = document.createElement("stzh-link");
        link.label = this.localization.moreInformation;
        link.href = options.url;
        link.target = "_blank";
        link.icon = "arrow-right";
        link.iconPosition = "right";
        link.size = "small";
        tooltipContentSlot.appendChild(link);

        tooltip.appendChild(tooltipContentSlot);
      } else {
        const tooltipContentSlot = document.createElement("div");
        tooltipContentSlot.slot = "content";

        const text = document.createElement("p");
        if (options.text.length >= 40) {
          text.style["textWrapMode"] = "wrap";
          text.style["textWrap"] = "wrap";
          text.style.minWidth = "200px";
        }
        text.style.margin = "0";
        text.textContent = options.text;
        tooltipContentSlot.appendChild(text);
        tooltip.appendChild(tooltipContentSlot);
      }

      tooltip.appendChild(element);
      hotspotDiv.appendChild(tooltip);
    } else {
      hotspotDiv.appendChild(element);
    }

    // prevent that you can do panorama actions with arrow keys, zooming with shift etc.
    element.addEventListener("keydown", event => {
      event.stopPropagation();
    });
  };

  private handleSceneDropdownChange = async (event: CustomEvent<StzhDropdownChangeEvent>) => {
    const target: HTMLStzhDropdownElement = event.target as HTMLStzhDropdownElement;
    const scene = (await target.getValue()) as string;
    this.scene = scene;
  };

  private handleControlsKeydown = (event: KeyboardEvent) => {
    // prevent that you can do panorama actions with arrow keys, zooming with shift etc.
    event.stopPropagation();
  };

  private action = (
    type:
      | "zoomIn"
      | "zoomOut"
      | "fullscreen"
      | "startOrientation"
      | "stopOrientation"
      | "panUp"
      | "panDown"
      | "panLeft"
      | "panRight"
  ) => {
    if (!["startOrientation", "fullscreen"].includes(type)) {
      this.stopOrientation();
    }

    if (type === "zoomIn") {
      this.executeAction("zoomIn", this.viewer.getHfov, this.viewer.setHfov, -10);
    } else if (type === "zoomOut") {
      this.executeAction("zoomOut", this.viewer.getHfov, this.viewer.setHfov, +10);
    } else if (type === "fullscreen") {
      this.viewer.toggleFullscreen();
    } else if (type === "startOrientation") {
      this.startOrientation();
    } else if (type === "panUp") {
      this.executeAction("panUp", this.viewer.getPitch, this.viewer.setPitch, +10);
    } else if (type === "panDown") {
      this.executeAction("panDown", this.viewer.getPitch, this.viewer.setPitch, -10);
    } else if (type === "panLeft") {
      this.executeAction("panLeft", this.viewer.getYaw, this.viewer.setYaw, -10);
    } else if (type === "panRight") {
      this.executeAction("panRight", this.viewer.getYaw, this.viewer.setYaw, +10);
    }
  };

  private stopOrientation() {
    if (this.viewer.isOrientationActive()) {
      this.viewer.stopOrientation();

      // trigger touchstart event to reset panorama rotation correctly
      // old preview code:
      // const evt = document.createEvent("HTMLEvents");
      // evt.initEvent("touchstart", false, true);
      // $(document).find(".pnlm-dragfix").get(0).dispatchEvent(evt);
      // alternative: https://gist.github.com/morewry/538efb737ed9c4e432e4

      var e = new window.Event("touchstart");
      this.element.querySelector(".pnlm-dragfix").dispatchEvent(e);

      this.updateOrientationState();
    }
  }

  private startOrientation() {
    if (this.viewer.isOrientationSupported()) {
      this.viewer.startOrientation();
      this.updateOrientationState();
    }
  }

  private updateOrientationState() {
    this.isOrientationSupported = this.viewer.isOrientationSupported();
    this.isOrientationActive = this.isOrientationSupported && this.viewer.isOrientationActive();
  }

  /**
   * Execute a button control action.
   * If the action is already runing/animating, add the value to the current position and set as new target.
   */
  private executeAction(name, getter, setter, value) {
    this.actionRunning[name] =
      typeof this.actionRunning[name] !== "undefined" && this.actionRunning[name] !== 0
        ? this.actionRunning[name] + value
        : getter() + value;

    setter(this.actionRunning[name], this.controlAnimationTime, () => {
      this.actionRunning = { [name]: 0 };
    });
  }

  private initViewer() {
    this.viewer = window.pannellum.viewer(
      this.containerElement,
      // https://pannellum.org/documentation/reference/
      {
        scenes: this._scenesLibrary,
        default: {
          firstScene: this.scene,
          showControls: false, // we show our own controls (zoom in/out and fullscreen)
          sceneFadeDuration: this.sceneFadeDuration,
          autoLoad: this.autoload,
          compass: false,
          orientationOnByDefault: this.orientationOnByDefault,
          strings: {
            loadButtonLabel: this.localization.loadButtonLabel,
            loadingLabel: this.localization.loadingLabel,
            bylineLabel: this.localization.bylineLabel,
            noPanoramaError: this.localization.noPanoramaError,
            fileAccessError: this.localization.fileAccessError,
            malformedURLError: this.localization.malformedURLError,
            iOS8WebGLError: this.localization.iOS8WebGLError,
            genericWebGLError: this.localization.genericWebGLError,
            textureSizeError: this.localization.textureSizeError,
            unknownError: this.localization.unknownError,
          },
        },
      }
    );

    this.viewer.on("load", () => {
      if (this.viewer) {
        // update classes for orientation control when a new scene has been loaded
        this.updateOrientationState();
        this.scene = this.viewer.getScene();

        this.stzhLoad.emit({
          component: "stzh-panorama",
        });
      }
    });

    this.viewer.on("touchstart", () => {
      // update classes for orientation control when user interacts with panorama
      this.updateOrientationState();
    });

    this.viewer.on("fullscreenchange", (status: boolean) => {
      this.isFullscreen = status;

      this.stzhFullscreenChange.emit({
        component: "stzh-panorama",
        value: this.isFullscreen,
      });
    });

    this.viewer.on("scenechange", (sceneId: string) => {
      // set focus back to container when scene has been changed
      this.containerElement.focus();

      this.stzhSceneChange.emit({
        component: "stzh-panorama",
        value: sceneId,
      });
    });

    this.viewer.on("zoomchange", (zoom: number) => {
      this.stzhZoomChange.emit({
        component: "stzh-panorama",
        value: zoom,
      });
    });

    this.viewer.on("animatefinished", (data: { pitch: number; yaw: number; hfov: number }) => {
      this.stzhChange.emit({
        component: "stzh-panorama",
        value: data,
      });
    });
  }

  async componentWillLoad() {
    if (!this.localization) {
      this.localization = await window.stzhComponents.utils.fetchTranslations(this.element, "panorama");
    }

    this.scenesWatcher(this.scenes);
  }

  async componentDidRender() {
    if (this.viewer) {
      return;
    }

    this.initViewer();
  }

  disconnectedCallback() {
    if (this.viewer) {
      this.viewer.destroy();
      this.viewer = null;
    }
  }

  render() {
    const classes = {
      "stzh-panorama": true,
      "stzh-panorama--is-motion": this.isOrientationActive,
      "stzh-panorama--is-fullscreen": this.isFullscreen,
    };

    const sceneOptions = this._scenes.map(({ sceneId, imageTitle }) => ({
      value: sceneId,
      text: imageTitle,
    }));

    const currentScene = this._scenes.find(({ sceneId }) => sceneId === this.scene);

    return (
      <Host>
        <div class={classes}>
          <stzh-figure
            class="stzh-panorama__figure"
            heading={this.heading}
            caption={currentScene.caption || this.caption}
            copyright={currentScene.copyright || this.copyright}
          >
            <stzh-ratio>
              <div
                ref={el => (this.containerElement = el as HTMLElement)}
                class="stzh-panorama__container"
                tabindex="0"
              >
                <div class="stzh-panorama__sceneselector">
                  {sceneOptions.length > 1 && (
                    <stzh-dropdown
                      class={"stzh-panorama__sceneselector-dropdown"}
                      size={"small"}
                      popoverPlacement={"top"}
                      label={this.localization.sceneLabel}
                      labelHidden={true}
                      noSearch={true}
                      items={[this.scene]}
                      options={sceneOptions}
                      onStzhChange={this.handleSceneDropdownChange}
                    ></stzh-dropdown>
                  )}
                  {(currentScene.imageAuthor || currentScene.copyright || this.copyright) && (
                    <div class="stzh-panorama__copyright">
                      {currentScene.imageAuthor || currentScene.copyright || this.copyright}
                    </div>
                  )}
                </div>
                <div class="stzh-panorama__controls" onKeyDown={this.handleControlsKeydown}>
                  {this.showControls && (
                    <Fragment>
                      <stzh-button
                        class="stzh-panorama__control stzh-panorama__control--show-medium"
                        iconOnly={true}
                        a11y-label={this.localization.panLeft}
                        variant="secondary"
                        icon="arrow-left"
                        size="small"
                        onClick={() => this.action("panLeft")}
                      ></stzh-button>
                      <stzh-button
                        class="stzh-panorama__control stzh-panorama__control--show-medium"
                        iconOnly={true}
                        a11y-label={this.localization.panRight}
                        variant="secondary"
                        icon="arrow-right"
                        size="small"
                        onClick={() => this.action("panRight")}
                      ></stzh-button>
                      <stzh-button
                        class="stzh-panorama__control stzh-panorama__control--show-medium"
                        iconOnly={true}
                        a11y-label={this.localization.panUp}
                        variant="secondary"
                        icon="arrow-up"
                        size="small"
                        onClick={() => this.action("panUp")}
                      ></stzh-button>
                      <stzh-button
                        class="stzh-panorama__control stzh-panorama__control--show-medium"
                        iconOnly={true}
                        a11y-label={this.localization.panDown}
                        variant="secondary"
                        icon="arrow-down"
                        size="small"
                        onClick={() => this.action("panDown")}
                      ></stzh-button>
                      <stzh-button
                        class="stzh-panorama__control stzh-panorama__control--show-medium"
                        iconOnly={true}
                        a11y-label={this.localization.zoomIn}
                        variant="secondary"
                        icon="plus"
                        size="small"
                        onClick={() => this.action("zoomIn")}
                      ></stzh-button>
                      <stzh-button
                        class="stzh-panorama__control stzh-panorama__control--show-medium"
                        iconOnly={true}
                        a11y-label={this.localization.zoomOut}
                        variant="secondary"
                        icon="minus"
                        size="small"
                        onClick={() => this.action("zoomOut")}
                      ></stzh-button>
                    </Fragment>
                  )}

                  {this.isOrientationSupported && (
                    <stzh-button
                      class="stzh-panorama__control"
                      variant="secondary"
                      a11yLabel={
                        this.isOrientationActive
                          ? this.localization.stopOrientation
                          : this.localization.startOrientation
                      }
                      icon={this.isOrientationActive ? "target-bullet" : "target"}
                      iconOnly={true}
                      size="small"
                      onClick={() => this.action(this.isOrientationActive ? "stopOrientation" : "startOrientation")}
                    ></stzh-button>
                  )}

                  <stzh-button
                    class="stzh-panorama__control"
                    variant="secondary"
                    a11yLabel={
                      this.isFullscreen
                        ? this.localization.disableFullscreenLabel
                        : this.localization.enableFullscreenLabel
                    }
                    icon={this.isFullscreen ? "shrink" : "expand"}
                    iconOnly={true}
                    size="small"
                    onClick={() => this.action("fullscreen")}
                  ></stzh-button>
                </div>
              </div>
            </stzh-ratio>
          </stzh-figure>
        </div>
      </Host>
    );
  }
}
